<template>
    <div></div>
</template>
<script>
export default {
    name:"CastPlayer",
    props: {
        localPlayerInstance: {
            type: Object,
            default: null
        },
        meta: {
            type: Object,
            default: null
        },
        localPlayerState: {
            type: Object,
            default: null
        },
        sourceUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            remotePlayer: null,
            remotePlayerController: null,
            remotePlayerState: null,
            PLAYER_STATE: {
                // No media is loaded into the player. For remote playback, maps to
                // the PlayerState.IDLE state.
                IDLE: 'IDLE',
                // Player is in PLAY mode but not actively playing content. For remote
                // playback, maps to the PlayerState.BUFFERING state.
                BUFFERING: 'BUFFERING',
                // The media is loaded but not playing.
                LOADED: 'LOADED',
                // The media is playing. For remote playback, maps to the PlayerState.PLAYING state.
                PLAYING: 'PLAYING',
                // The media is paused. For remote playback, maps to the PlayerState.PAUSED state.
                PAUSED: 'PAUSED'
            }
        }
    },
    computed: {
        playerHandler() {
            return this.setupPlayerHandler()
        },
        remoteTraget() {
            return this.setupRemoteTarget();
        },
        localTarget() {
            return this.setupLocalTarget();
        },
        castConfig() {
            return {
                receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
                autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
                androidReceiverCompatible: true
            }
        },
        castSessionStates(){
            return cast.framework.SessionState
        },
        currentCastSession() {
            return cast.framework.CastContext.getInstance().getCurrentSession();
        },
        currentCastSessionState(){
            if(!this.currentCastSession) return null;

            return this.currentCastSession.getSessionState();
        },
        mediaInfoIfActiveSession() {
            if(!this.currentCastSession) return null;

            return this.currentCastSession.getMediaSession();
        },
    },
    mounted(){
        this.initialiseRemotePlayer();
        this.configerRemotePlayerListeners();
        this.addCastIconToLocalPlayer()
    },
    methods: {
        /**
        *===========================================================================================
        *                          REMOTE PLAYER AND CONTROLLER INITIALIZATION
        *===========================================================================================
        */
        initialiseRemotePlayer() {
            cast.framework.CastContext.getInstance().setOptions(this.castConfig);
            this.remotePlayer = new cast.framework.RemotePlayer();
            this.remotePlayerController = new cast.framework.RemotePlayerController(this.remotePlayer);
        },

        /**
        *===========================================================================================
        *                               CONFIGURE REMOTE PLAYER LISTNERS
        *===========================================================================================
        */
        configerRemotePlayerListeners() {

            /**
             * When cast device is connected/disconnected (connection changed event) then switch player
             * and configure cast and local player
             */
            this.remotePlayerController.addEventListener(
                cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
                function (e) {
                    this.onConnectivityChange(e);
                }.bind(this)
            );

            // Triggers when the media info or the player state changes
            this.remotePlayerController.addEventListener(
                cast.framework.RemotePlayerEventType.MEDIA_INFO_CHANGED,
                function (event) {
                    this.onMediaChange(event)
                }.bind(this)
            );

            this.remotePlayerController.addEventListener(
                cast.framework.RemotePlayerEventType.IS_PAUSED_CHANGED,
                function (event) {
                    this.onRemotePlayerPlayPause(event);
                }.bind(this)
            );

            this.remotePlayerController.addEventListener(
                cast.framework.RemotePlayerEventType.IS_MUTED_CHANGED,
                function (event) {
                    this.onMutedChange(event)
                }.bind(this)
            );
        },

        /**
        *===========================================================================================
        *                               LOCAL PLAYER CONTROLS MENIPULATION
        *===========================================================================================
        */
        // Add cast launcher icon to local player
        addCastIconToLocalPlayer(){
            this.localPlayerInstance.controls.add({
                position: "right",
                html: '<google-cast-launcher style="height: 15px"></google-cast-launcher>',
            });

        },

        /**
         *===========================================================================================
        *                               REMOTE PLAYER EVENT HANDELERS
        *===========================================================================================
        */
        /**
         * Here conectivity means whenever whenever a video is pushed or pulled from chromecast device
         * so if we are pushing a video to chromecast then it means chromecast is ONLINE and whenever
         * a video is removed from chromecast(stop casting) it means chrome cast will goin to be OFFLINE
         */
        onConnectivityChange(event) {
            if (cast && cast.framework && this.remotePlayer.isConnected) {

                if(this.currentCastSessionState==this.castSessionStates.SESSION_RESUMED){
                    return this.playerHandler.prepareToPlay(this.sourceUrl);
                }

                return this.playerHandler.load(this.sourceUrl);

            } else {
                console.error('Remote player is not connected');
            }
        },

        /**
         * Remote player (cast device): triggers event whenever media url or meta data of media
         * (title, description, currentTime, etc) changed
         */
        onMediaChange(event) {
            if (!this.currentCastSession) return

            if (!this.mediaInfoIfActiveSession) return

            /**
             * Reload player only if localMaintainedState and actualStateOfRemotePlayer has
             * been mismatched, else no action is required
             */
            if (this.mediaInfoIfActiveSession.playerState == this.PLAYER_STATE.PLAYING && this.remotePlayerState !== this.PLAYER_STATE.PLAYING) {
                this.playerHandler.prepareToPlay(this.sourceUrl);
            }
        },

        /**
        * Chromecast does not have multiple dedicated events of play and pause, they
        * provide single playOrPause event for play and pause handeling, that's why
        * this logic handeled this way
        **/
        onRemotePlayerPlayPause(event) {
            if (this.remotePlayer.isPaused) {
                this.playerHandler.pause();
            } else if (this.remotePlayerState !== this.PLAYER_STATE.PLAYING) {
                // If currently not playing, start to play.
                // This occurs if starting to play from local, but this check is
                // required if the state is changed remotely.
                this.playerHandler.play();
            }
        },

        /**
         * Remote Player mute/unmute handeler
         */
        onMutedChange(event) {
            if (this.remotePlayer.isMuted) {
                return this.playerHandler.mute();
            }

            return this.playerHandler.unMute();

        },

        /**
        *===========================================================================================
        *                               PLAYER HANDELER METHODS SETUP
        *===========================================================================================
        * Player handeler is like a bridge between remote player and local player, this handeler is reponsible for
        * handeling remote player target and local player target
        */
        setupPlayerHandler() {
            let handler = {};

            handler.play = function (sourceUrl) {
                if (this.remotePlayerState == this.PLAYER_STATE.IDLE || !this.remoteTraget.isMediaLoaded()) {
                    this.remoteTraget.load(sourceUrl, this.meta);
                    return;
                }

                this.remotePlayerState = this.PLAYER_STATE.PLAYING;
                this.remoteTraget.play();
                this.localTarget.pause();
            }.bind(this);

            handler.pause = function () {
                this.remoteTraget.pause();
                this.remotePlayerState = this.PLAYER_STATE.PAUSED;
                // Put action for localPlayer (browser) IF REQUIRED
            }.bind(this);

            handler.stop = function () {
                this.remotePlayerState = this.PLAYER_STATE.IDLE;
                this.remoteTraget.stop();
            }.bind(this);

            handler.load = function (sourceUrl) {
                this.remotePlayerState = this.PLAYER_STATE.BUFFERING;
                this.remoteTraget.load(sourceUrl, this.meta);
            }.bind(this);

            handler.prepareToPlay = function (sourceUrl) {
                this.remotePlayerState = this.PLAYER_STATE.LOADED;
                handler.play(sourceUrl);
            }.bind(this);

            handler.mute = function () {
                this.remoteTraget.mute();
                this.localTarget.mute()
            }.bind(this);

            handler.unMute = function () {
                this.remoteTraget.unMute();
            }.bind(this);

            handler.seekTo = function (time) {
                this.remoteTraget.seekTo(time);
            }.bind(this);

            return handler;
        },

        /**
        *===========================================================================================
        *                               REMOTE PLAYER MANIPULATOR
        *===========================================================================================
        * this target is responsible to give instruction to remote player instace, it meanse this target
        * is responsible for play, pause, mute, etc of remote player
        */
        setupRemoteTarget() {
            // This object will implement PlayerHandler callbacks with
            // remotePlayerController, and makes necessary updates specific
            // to remote playback.
            var playerTarget = {};

            playerTarget.play = function () {
                if (this.remotePlayer.isPaused) {
                    this.remotePlayerController.playOrPause();
                }
            }.bind(this);

            playerTarget.pause = function () {
                /**
                 * Chromecast does not have multiple dedicated events of play and pause, they
                 * provide single playOrPause event for play and pause handeling
                 *
                 * THIS LOGIC WILL ALWAYS LEAD TO PAUSE THE REMOTE PLAYER
                 */
                if (!this.remotePlayer.isPaused) {
                    this.remotePlayerController.playOrPause();
                }
            }.bind(this);

            playerTarget.stop = function () {
                this.remotePlayerController.stop();
            }.bind(this);

            playerTarget.isMediaLoaded = function () {
                if (!this.currentCastSession) return false;
                if (!this.mediaInfoIfActiveSession) return false;

                if (this.mediaInfoIfActiveSession.playerState == this.PLAYER_STATE.IDLE) return false;

                return true;
            }.bind(this);

            /**
             * @return {number?} Current media time for the content. Always returns
             *      media time even if in clock time (conversion done when displaying).
             */
            playerTarget.getCurrentMediaTime = function () {
                if (this.isLiveContent && this.mediaInfo.metadata &&
                    this.mediaInfo.metadata.sectionStartTimeInMedia) {
                    return this.remotePlayer.currentTime - this.mediaInfo.metadata.sectionStartTimeInMedia;
                } else {
                    // VOD and live scenerios where live metadata is not provided.
                    return this.remotePlayer.currentTime;
                }
            }.bind(this);


            playerTarget.mute = function () {
                if (!this.remotePlayer.isMuted) {
                    this.remotePlayerController.muteOrUnmute();
                }
            }.bind(this);

            playerTarget.unMute = function () {
                if (this.remotePlayer.isMuted) {
                    this.remotePlayerController.muteOrUnmute();
                }
            }.bind(this);


            playerTarget.seekTo = function (time) {
                this.remotePlayer.currentTime = time;
                this.remotePlayerController.seek();
            }.bind(this);

            playerTarget.load = async function (sourceUrl,metaData) {
                // let mediaSource = await this.getExternalSrcUrl();

                let mediaInfo = new chrome.cast.media.MediaInfo(sourceUrl, 'video/mp4');
                mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED;
                mediaInfo.metadata = new chrome.cast.media.TvShowMediaMetadata();
                mediaInfo.metadata.title = metaData.title;
                mediaInfo.metadata.subtitle = metaData.description;
                // mediaInfo.metadata.images = [{
                //     'url': MEDIA_SOURCE_ROOT + this.mediaContents[mediaIndex]['thumb']
                // }];

                let request = new chrome.cast.media.LoadRequest(mediaInfo);
                request.currentTime = this.currentTime;

                // Do not immediately start playing if the player was previously PAUSED.
                // if (!this.playerStateBeforeSwitch || this.playerStateBeforeSwitch == this.PLAYER_STATE.PAUSED) {
                //     request.autoplay = false;
                // } else {
                //     request.autoplay = true;
                // }
                cast.framework.CastContext.getInstance().getCurrentSession().loadMedia(request).then(
                    function () {
                        console.log('Remote media loaded');
                    }.bind(this),
                    function (errorCode) {
                        this.remotePlayerState = this.PLAYER_STATE.IDLE;

                    }.bind(this));

            }.bind(this)

            return playerTarget;
        },

        /**
        *===========================================================================================
        *                               LOCAL PLAYER MANIPULATOR
        *===========================================================================================
        * this target is responsible to give instruction to local player instace, it meanse this target
        * is responsible for play, pause, mute, etc of local player
        */
        setupLocalTarget() {
            var localTarget = {}

            localTarget.play = () => {
                if(!this.localPlayerInstance.playing){
                    this.localPlayerInstance.play()
                }
            }

            localTarget.pause = () => {
                if(this.localPlayerInstance.playing){
                    this.localPlayerInstance.pause()
                }
            }

            localTarget.mute = () => {
                if(!this.localPlayerInstance.muted){
                    this.localPlayerInstance.muted = true
                }
            }

            localTarget.unmute = () => {
                if(this.localPlayerInstance.muted){
                    this.localPlayerInstance.muted = false
                }
            }

            return localTarget;
        }
    }
}
</script>
